import { AlertV2, SimpleAlert, Spinner } from '@cimplico/components';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExchangeXeroCode } from '../../queries/use-exchange-xero-code';
import { useXeroConsentUrl } from '../../queries/use-xero-consent-url';
import { XeroTenantConnection } from '../../types/xero';
import { useUserContext } from '../../providers/UserProvider';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import ConnectToXero from '../../components/connect-to-xero/ConnectToXero';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import XeroEntitiesForm from './bulk-onboard-xero-entities/XeroEntitiesForm';

function XeroForm(): JSX.Element {
    const [params, setParams] = useSearchParams();
    const [connections, setConnections] = useState<XeroTenantConnection[] | undefined>();
    const [xeroUserId, setXeroUserId] = useState<string | undefined>();

    const code = params.get('code');
    const xeroConsentUrlQuery = useXeroConsentUrl();
    const exchangeXeroCodeQuery = useExchangeXeroCode();

    const user = useUserContext();
    const { isAdmin } = useIsFirmRole();

    const xeroReferWarning = user.xero?.xeroRefer && !isAdmin;

    useEffect(() => {
        if (code) {
            setParams({ tab: 'xero' });

            exchangeXeroCodeQuery.mutate(code, {
                onSuccess: ({ connections, xeroUserId }) => {
                    setConnections(connections);
                    setXeroUserId(xeroUserId);
                },
            });
        }
    }, [code, exchangeXeroCodeQuery, setParams]);

    async function redirectToXero(url: string): Promise<void> {
        window.location.href = url;
    }

    if (exchangeXeroCodeQuery.isLoading) {
        return (
            <div className="flex flex-col items-center justify-center gap-1">
                <Spinner />
                <p className="text-sm text-gray-500">Loading...</p>
            </div>
        );
    }

    if (connections && connections.length === 0) {
        return (
            <div className="my-16">
                <EmptyStateNew
                    title="No Xero Entities"
                    helpText="All entities may have already been onboarded."
                    icon={<BuildingOfficeIcon />}
                />
            </div>
        );
    }

    if (connections && xeroUserId) {
        return <XeroEntitiesForm xeroUserId={xeroUserId} connections={connections} onCancel={(): void => setConnections(undefined)} />;
    }

    return (
        <div>
            {xeroReferWarning && (
                <div className="my-4">
                    <AlertV2
                        type="warn"
                        description="Your account has been flagged as a Xero app store referral. As you are NOT the firm admin, you will not be able to connect a Xero entity. To resolve this issue, please contact support."
                    />
                </div>
            )}
            {xeroConsentUrlQuery.error && (
                <div className="my-4">
                    <SimpleAlert type="error" title={(xeroConsentUrlQuery.error as AxiosError)?.response?.data.message} />
                </div>
            )}
            <div className="pb-4">
                <AlertV2
                    type="warn"
                    title="Note"
                    description="Please ensure you have 'Reports' access in Xero. Authorising without access to Reports will prevent the trial balance from being pulled."
                />
            </div>
            <ConnectToXero onUrl={redirectToXero} disabled={xeroReferWarning} />
        </div>
    );
}
export default XeroForm;
